import { shallowRef, ref, type Ref, type ShallowRef } from 'vue'
import { useDebounceFn } from '@vueuse/core'

/**
 * Naming stance (plan task 1.3): the shared reactive layer standardizes on
 * `state/<domain>-datasource.ts` exporting a `<domain>RefDataSource` singleton
 * built here. The public `composables/useSelect*DataSource` hooks keep their
 * existing names and return shapes (non-negotiable: no public API change), so
 * the `useSelect*` ⇄ `*RefDataSource` infix split is a conscious, documented
 * tradeoff rather than drift.
 */

/** Options for {@link createListRefDataSource}. */
export interface CreateListRefDataSourceOptions<T> {
  /** Network fetch for the full reference list. */
  fetch: () => Promise<T[]>
  /**
   * Identity key for an item. Required to use `get`, `upsert`, or `remove`;
   * omit for list-only sources (company / port-call type / estimated time).
   */
  getKey?: (item: T) => string
  /**
   * Debounce window (ms) coalescing rapid `fetch` calls. Omit for an immediate
   * fetch (currency relies on the synchronous-after-await timing); pass `0` to
   * mirror the list sources' trailing-edge dedup.
   */
  debounceMs?: number
}

/** A shared, module-level reference-data list cache. */
export interface ListRefDataSource<T> {
  /** The cached list, or `undefined` until first fetched. */
  items: ShallowRef<T[] | undefined>
  /** True while a fetch is in flight. */
  loading: Ref<boolean>
  /** Fetch once and cache; pass `true` to force a re-request. */
  fetch: (force?: true) => Promise<void>
  /** Ensure the list is loaded, then find an item by key. */
  get: (key: string) => Promise<T | undefined>
  /** Insert or replace an item by key (no-op without `getKey`). */
  upsert: (item: T) => void
  /** Drop an item by key (no-op without `getKey`). */
  remove: (key: string) => void
  /** Reset the cache so the next `fetch` re-requests. */
  clear: () => void
}

/**
 * Builds a shared (module-level singleton) cache for a flat reference-data list
 * consumed by select / combobox pickers. Every caller of the wrapping
 * `useSelect*DataSource` hook shares one cache, fetch-once dedup, and (where
 * wired) SignalR-driven invalidation — equivalent to the pre-`shared-breakup`
 * Pinia ref store, without the store.
 *
 * Call this once at module scope inside a `state/<domain>-datasource.ts` file.
 */
export function createListRefDataSource<T>(
  options: CreateListRefDataSourceOptions<T>
): ListRefDataSource<T> {
  const { getKey } = options
  const items = shallowRef<T[] | undefined>(undefined)
  const loading = ref(false)
  const fetched = ref(false)

  async function runFetch(force?: true) {
    if (fetched.value && !force) return
    fetched.value = true
    loading.value = true
    try {
      items.value = await options.fetch()
    } finally {
      loading.value = false
    }
  }

  const fetch: (force?: true) => Promise<void> =
    options.debounceMs === undefined
      ? runFetch
      : (() => {
          const debounced = useDebounceFn(runFetch, options.debounceMs)
          return (force?: true) => debounced(force).then(() => undefined)
        })()

  async function get(key: string) {
    await fetch()
    if (!items.value || !getKey) return undefined
    return items.value.find((item) => getKey(item) === key)
  }

  function upsert(item: T) {
    if (!getKey) return
    const key = getKey(item)
    const list = items.value
    if (!list) {
      items.value = [item]
      return
    }
    const idx = list.findIndex((existing) => getKey(existing) === key)
    if (idx >= 0) {
      const next = list.slice()
      next[idx] = item
      items.value = next
    } else {
      items.value = [...list, item]
    }
  }

  function remove(key: string) {
    if (!items.value || !getKey) return
    items.value = items.value.filter((item) => getKey(item) !== key)
  }

  function clear() {
    fetched.value = false
    items.value = undefined
    loading.value = false
  }

  return { items, loading, fetch, get, upsert, remove, clear }
}
